import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { UseChatReturn } from '../hooks/useChat';
import type { SelectorResult, SelectorType } from '../selectors/registry';
import type { PartialDesignBrief } from '../types/briefSchema';
import type { Message } from '../types/chat';
import type { UploadedAttachment } from '../types/selectors';

// ─── Slice shapes ────────────────────────────────────────────────────────────
// The chat state and actions are split into three focused contexts:
//
//   • Messages — read-only conversation log + loading/error display state
//   • Input    — typing state for the composer (text + attachment + submit)
//   • Actions  — higher-level operations (selector completion, dismiss, gates)
//
// `ChatProvider` remains the single public entrypoint: it takes the full
// `UseChatReturn` and fans it out across the three slice contexts. This keeps
// `ChatInterface` and existing tests untouched while letting consumers
// migrate to the narrower `useChatMessages` / `useChatInput` / `useChatActions`
// hooks.

export interface ChatMessagesValue {
    messages: Message[];
    isLoading: boolean;
    error: string | null;
    isStaleSession: boolean;
    isFatalError: boolean;
    /** True once the user has sent at least one message in this session. */
    hasUserSent: boolean;
    messagesEndRef: React.Ref<HTMLDivElement>;
    retryCount: number;
    /** Maximum number of user messages allowed in this chat session. Null if unknown. */
    maxMessages: number | null;
}

export interface ChatInputValue {
    inputText: string;
    onInputChange: (text: string) => void;
    attachments: UploadedAttachment[];
    onAttachmentsAdd: (added: UploadedAttachment[]) => void;
    onAttachmentRemove: (uploadId: string) => void;
    onSend: () => Promise<void>;
    isLoading: boolean;
}

export interface ChatActionsValue {
    briefComplete: boolean;
    sendText: (content: string) => Promise<void>;
    onSelectorComplete: <T extends SelectorType>(
        type: T,
        result: SelectorResult<T>
    ) => Promise<void>;
    onRetry: () => Promise<void>;
    printProductMpvId: string;
    designBrief: PartialDesignBrief;
}

const ChatMessagesContext = createContext<ChatMessagesValue | null>(null);
const ChatInputContext = createContext<ChatInputValue | null>(null);
const ChatActionsContext = createContext<ChatActionsValue | null>(null);

export interface ChatProviderProps {
    value: UseChatReturn;
    printProductMpvId: string;
    designBrief?: PartialDesignBrief;
    children: ReactNode;
}

/**
 * Composes the three focused contexts from a single `UseChatReturn`. Kept as
 * the only provider so callers (and tests) don't need to wire three providers
 * by hand.
 */
export function ChatProvider({
    value,
    printProductMpvId,
    designBrief = {},
    children,
}: ChatProviderProps) {
    const messages = useMemo<ChatMessagesValue>(
        () => ({
            messages: value.messages,
            isLoading: value.isLoading,
            error: value.error,
            isStaleSession: value.isStaleSession,
            isFatalError: value.isFatalError,
            hasUserSent: value.hasUserSent,
            messagesEndRef: value.messagesEndRef,
            retryCount: value.retryCount,
            maxMessages: value.maxMessages,
        }),
        [
            value.messages,
            value.isLoading,
            value.error,
            value.isStaleSession,
            value.isFatalError,
            value.hasUserSent,
            value.messagesEndRef,
            value.retryCount,
            value.maxMessages,
        ]
    );

    const input = useMemo<ChatInputValue>(
        () => ({
            inputText: value.inputText,
            onInputChange: value.onInputChange,
            attachments: value.attachments,
            onAttachmentsAdd: value.onAttachmentsAdd,
            onAttachmentRemove: value.onAttachmentRemove,
            onSend: value.onSend,
            isLoading: value.isLoading,
        }),
        [
            value.inputText,
            value.onInputChange,
            value.attachments,
            value.onAttachmentsAdd,
            value.onAttachmentRemove,
            value.onSend,
            value.isLoading,
        ]
    );

    const actions = useMemo<ChatActionsValue>(
        () => ({
            briefComplete: value.briefComplete,
            sendText: value.sendText,
            onSelectorComplete: value.onSelectorComplete,
            onRetry: value.retry,
            printProductMpvId,
            designBrief,
        }),
        [
            value.briefComplete,
            value.sendText,
            value.onSelectorComplete,
            value.retry,
            printProductMpvId,
            designBrief,
        ]
    );

    return (
        <ChatMessagesContext.Provider value={messages}>
            <ChatInputContext.Provider value={input}>
                <ChatActionsContext.Provider value={actions}>
                    {children}
                </ChatActionsContext.Provider>
            </ChatInputContext.Provider>
        </ChatMessagesContext.Provider>
    );
}

function required<T>(ctx: T | null, hookName: string): T {
    if (!ctx) throw new Error(`${hookName} must be used within ChatProvider`);
    return ctx;
}

export function useChatMessages(): ChatMessagesValue {
    return required(useContext(ChatMessagesContext), 'useChatMessages');
}

export function useChatInput(): ChatInputValue {
    return required(useContext(ChatInputContext), 'useChatInput');
}

export function useChatActions(): ChatActionsValue {
    return required(useContext(ChatActionsContext), 'useChatActions');
}
